import { Directive, OnInit, EventEmitter, OnDestroy, ElementRef, Output, Input, NgModule, Host, Inject, Self, Optional, AfterViewInit } from "@angular/core";
import { InjectionToken } from '@angular/core';


export interface WLazyLoadable {
    isVisible: boolean;
    isLazyLoading: boolean;
    visibilityChange: EventEmitter<boolean>;
}
export const W_LAZY_LOADABLE = new InjectionToken<WLazyLoadable>('WLazyLoadable');

const observedElements = new Map<Element, (isVisible: boolean) => void>();
let observer: IntersectionObserver;
export const observeIntersection = (callback: (isVisible: boolean) => void, element: Element) => {
    if (!observer) {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const callback = observedElements.get(entry.target);
                        if (callback) {
                            callback(entry.isIntersecting);
                        }
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '50px 0px'
            }
        );
    }

    observedElements.set(element, callback);
    observer.observe(element);
}
export const unobserveIntersection = (element: Element) => {
    observer && observer.unobserve(element);
    observedElements.delete(element);
}
@Directive({
    selector: '[outOfViewportHeight]',
    standalone: false
})
export class WLazyLoadDirective implements OnInit, AfterViewInit, OnDestroy {
    @Output() wVisibile = new EventEmitter<boolean>();
    @Input() outOfViewportHeight: number = 0;
    isVisible = false;
    private observing = false;

    constructor(protected elementRef: ElementRef,
        @Host() @Optional() @Inject(W_LAZY_LOADABLE) private host: WLazyLoadable,
    ) {
        if (host) host.isLazyLoading = true;
    }

    ngOnInit() {
        this.observing = (this.outOfViewportHeight ?? 0) > 0;
        if (this.observing) {
            observeIntersection((isVisible) => {
                this.notify(isVisible);
                unobserveIntersection(this.elementRef.nativeElement);
            }, this.elementRef.nativeElement);
        }
    }
    ngAfterViewInit(): void {
        // if height not set, emit and do not watch
        if (!this.observing) {
            this.notify(true);
        }
    }
    ngOnDestroy() {
        if (this.observing) unobserveIntersection(this.elementRef.nativeElement);
    }
    private notify(isVisible: boolean) {
        /** do not break if no host
         * we can use this on plain HTMLElement or component that does not provide injectable for W_LAZY_LOADABLE
         * */
        if (this.host) {
            this.host.visibilityChange.emit(isVisible);
            this.host.isVisible = isVisible;
        }
        this.isVisible = isVisible;
        this.wVisibile.emit(isVisible);
    }
}
@NgModule({
    exports: [WLazyLoadDirective],
    declarations: [WLazyLoadDirective]
})
export class WLazyLoadDirectiveModule { }